import { isEmpty } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { getEmptyState, hasEmptyState, mergeQueryParams, parseQueryParams, stringifyQueryParams } from './queryParams';
import type { ParsedUrlType, QueryParams, QueryParamsType } from './types';

interface UseQueryParamsResult<T> {
  clearAllQueryParams: () => void;
  pushWithQueryParams: (path: string, params: Partial<T>, includeExistingQueryString?: boolean) => void;
  queryParams: ParsedUrlType<T>;
  setQueryParams: (params: Partial<T>, includeExistingQueryString?: boolean) => void;
}

const getUrl = (pathName: string, queryString: string): string => {
  return `${pathName}${queryString}`;
};

const getQueryString = (params: object, existingQueryString: string = '', objectKey?: string): string => {
  if (!objectKey) {
    return stringifyQueryParams(params);
  }

  return mergeQueryParams(existingQueryString, { [objectKey]: params });
};

const useQueryParams = <T extends {}>(objectKey?: string, initialValues: Partial<T> = {}): UseQueryParamsResult<T> => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Store initial values in a ref to avoid dependency changes
  const initialValuesRef = useRef(initialValues);

  // Track if we've already called appendQueryParams to prevent duplicate calls
  const hasCalledAppendRef = useRef(false);

  const parsedQueryParams: QueryParams = parseQueryParams(searchParams.toString(), false);
  const queryStringParams: QueryParams = !objectKey ? parsedQueryParams : parsedQueryParams[objectKey];

  const appendQueryParams = useCallback(
    (params: Partial<T>): void => {
      // Read from window.location.search to get the actual current URL
      // This avoids race conditions when multiple hooks call setSearchParams in the same tick
      const currentSearch = window.location.search.slice(1); // Remove leading '?'
      const newQueryString = getQueryString(params, currentSearch, objectKey);

      navigate({ search: newQueryString }, { replace: true });
    },
    [navigate, objectKey]
  );

  // Initialize query params when URL doesn't have this objectKey's params
  useEffect(() => {
    const storedInitialValues = initialValuesRef.current;
    const hasParams = !isEmpty(queryStringParams);
    const hasInitialValues = !isEmpty(storedInitialValues);

    // Only initialize if:
    // 1. URL doesn't have params for this objectKey
    // 2. We have initial values to set
    // 3. We haven't already called appendQueryParams
    if (!hasParams && hasInitialValues && !hasCalledAppendRef.current) {
      hasCalledAppendRef.current = true;
      appendQueryParams(storedInitialValues);
    }
  }, [queryStringParams, appendQueryParams, objectKey]);

  const setQueryParams = (params: Partial<T>, includeExistingQueryString: boolean = true): void => {
    const queryParams: QueryParamsType<T> = isEmpty(params) ? getEmptyState() : params;

    pushWithQueryParams(pathname, queryParams, includeExistingQueryString);
  };

  const pushWithQueryParams = (path: string, params: QueryParamsType<T>, includeExistingQueryString: boolean = true): void => {
    const existingQueryString: string = includeExistingQueryString ? String(searchParams) : '';

    const queryString: string = getQueryString(params, existingQueryString, objectKey);

    navigate(getUrl(path, queryString));
  };

  const clearAllQueryParams = (): void => {
    return setQueryParams({}, false);
  };

  const getQueryParams = (): ParsedUrlType<T> => {
    const queryParams = queryStringParams || initialValues;
    const params = !hasEmptyState(queryParams) ? queryParams : {};

    return params as ParsedUrlType<T>;
  };

  return { queryParams: getQueryParams(), setQueryParams, clearAllQueryParams, pushWithQueryParams };
};

export default useQueryParams;
